import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Scale, TrendingUp, Target, Info } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface BMIResult {
  bmi: number;
  category: string;
  description: string;
  healthyWeightRange: { min: number; max: number };
  weightToLose?: number;
  weightToGain?: number;
}

const BMICalculator: React.FC = () => {
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [unit, setUnit] = useState<'metric' | 'imperial'>('metric');
  const [feet, setFeet] = useState('');
  const [inches, setInches] = useState('');
  const [result, setResult] = useState<BMIResult | null>(null);
  const [age, setAge] = useState('');
  const [gender, setGender] = useState<'male' | 'female'>('male');

  useSEO({
    title: 'BMI Calculator - Body Mass Index Calculator',
    description: 'Free online BMI calculator. Calculate your Body Mass Index and get health recommendations based on your weight and height.',
    keywords: 'BMI calculator, body mass index, weight calculator, health calculator, obesity calculator',
    canonical: 'https://snap-tools.online/calc/bmi-calculator'
  });

  const getBMICategory = (bmi: number): { category: string; description: string; color: string } => {
    if (bmi < 18.5) {
      return {
        category: 'Underweight',
        description: 'Below normal weight range. Consider consulting a healthcare provider.',
        color: 'blue'
      };
    } else if (bmi < 25) {
      return {
        category: 'Normal Weight',
        description: 'Within healthy weight range. Maintain current lifestyle.',
        color: 'green'
      };
    } else if (bmi < 30) {
      return {
        category: 'Overweight',
        description: 'Above normal weight range. Consider lifestyle changes.',
        color: 'yellow'
      };
    } else if (bmi < 35) {
      return {
        category: 'Obesity Class I',
        description: 'Moderately obese. Health risks may be increased.',
        color: 'orange'
      };
    } else if (bmi < 40) {
      return {
        category: 'Obesity Class II',
        description: 'Severely obese. Significant health risks.',
        color: 'red'
      };
    } else {
      return {
        category: 'Obesity Class III',
        description: 'Very severely obese. Consult healthcare provider immediately.',
        color: 'red'
      };
    }
  };

  const calculateBMI = (weightKg: number, heightM: number): BMIResult => {
    const bmi = weightKg / (heightM * heightM);
    const categoryInfo = getBMICategory(bmi);
    
    // Calculate healthy weight range (BMI 18.5-24.9)
    const minHealthyWeight = 18.5 * heightM * heightM;
    const maxHealthyWeight = 24.9 * heightM * heightM;
    
    let weightToLose: number | undefined;
    let weightToGain: number | undefined;
    
    if (bmi > 24.9) {
      weightToLose = weightKg - maxHealthyWeight;
    } else if (bmi < 18.5) {
      weightToGain = minHealthyWeight - weightKg;
    }

    return {
      bmi: parseFloat(bmi.toFixed(1)),
      category: categoryInfo.category,
      description: categoryInfo.description,
      healthyWeightRange: {
        min: parseFloat(minHealthyWeight.toFixed(1)),
        max: parseFloat(maxHealthyWeight.toFixed(1))
      },
      weightToLose,
      weightToGain
    };
  };

  const handleCalculate = () => {
    let weightKg: number;
    let heightM: number;

    // Convert weight to kg
    if (unit === 'metric') {
      weightKg = parseFloat(weight);
      if (isNaN(weightKg) || weightKg <= 0) {
        toast.error('Please enter a valid weight');
        return;
      }
    } else {
      const weightLbs = parseFloat(weight);
      if (isNaN(weightLbs) || weightLbs <= 0) {
        toast.error('Please enter a valid weight');
        return;
      }
      weightKg = weightLbs * 0.453592; // Convert lbs to kg
    }

    // Convert height to meters
    if (unit === 'metric') {
      const heightCm = parseFloat(height);
      if (isNaN(heightCm) || heightCm <= 0) {
        toast.error('Please enter a valid height');
        return;
      }
      heightM = heightCm / 100; // Convert cm to m
    } else {
      const feetVal = parseFloat(feet);
      const inchesVal = parseFloat(inches) || 0;
      if (isNaN(feetVal) || feetVal <= 0) {
        toast.error('Please enter a valid height');
        return;
      }
      const totalInches = feetVal * 12 + inchesVal;
      heightM = totalInches * 0.0254; // Convert inches to meters
    }

    const bmiResult = calculateBMI(weightKg, heightM);
    setResult(bmiResult);
  };

  useEffect(() => {
    if (weight && (unit === 'metric' ? height : feet)) {
      handleCalculate();
    } else {
      setResult(null);
    }
  }, [weight, height, feet, inches, unit]);

  const getBMIColor = (bmi: number): string => {
    if (bmi < 18.5) return 'text-blue-600';
    if (bmi < 25) return 'text-green-600';
    if (bmi < 30) return 'text-yellow-600';
    if (bmi < 35) return 'text-orange-600';
    return 'text-red-600';
  };

  const getBMIBgColor = (bmi: number): string => {
    if (bmi < 18.5) return 'bg-blue-50 border-blue-200';
    if (bmi < 25) return 'bg-green-50 border-green-200';
    if (bmi < 30) return 'bg-yellow-50 border-yellow-200';
    if (bmi < 35) return 'bg-orange-50 border-orange-200';
    return 'bg-red-50 border-red-200';
  };

  const formatWeight = (weightKg: number): string => {
    if (unit === 'metric') {
      return `${weightKg.toFixed(1)} kg`;
    } else {
      const weightLbs = weightKg * 2.20462;
      return `${weightLbs.toFixed(1)} lbs`;
    }
  };

  const bmiRanges = [
    { range: 'Below 18.5', category: 'Underweight', color: 'blue' },
    { range: '18.5 - 24.9', category: 'Normal Weight', color: 'green' },
    { range: '25.0 - 29.9', category: 'Overweight', color: 'yellow' },
    { range: '30.0 - 34.9', category: 'Obesity Class I', color: 'orange' },
    { range: '35.0 - 39.9', category: 'Obesity Class II', color: 'red' },
    { range: '40.0 and above', category: 'Obesity Class III', color: 'red' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-xl sm:rounded-2xl mb-4 sm:mb-6 shadow-lg">
            <Scale className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8438ff] mb-3 sm:mb-4">
            BMI Calculator
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Calculate your Body Mass Index (BMI) and get personalized health recommendations 
            based on your weight and height measurements.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
          className="mb-6 sm:mb-8"
        />

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Calculator */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Calculate Your BMI</h2>
              
              {/* Unit Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Measurement Unit
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="metric"
                      checked={unit === 'metric'}
                      onChange={(e) => setUnit(e.target.value as 'metric' | 'imperial')}
                      className="w-4 h-4 text-[#8438ff] border-gray-300 focus:ring-[#8438ff]"
                    />
                    <span className="ml-2 text-sm text-gray-700">Metric (kg, cm)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="imperial"
                      checked={unit === 'imperial'}
                      onChange={(e) => setUnit(e.target.value as 'metric' | 'imperial')}
                      className="w-4 h-4 text-[#8438ff] border-gray-300 focus:ring-[#8438ff]"
                    />
                    <span className="ml-2 text-sm text-gray-700">Imperial (lbs, ft/in)</span>
                  </label>
                </div>
              </div>

              {/* Weight Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weight {unit === 'metric' ? '(kg)' : '(lbs)'}
                </label>
                <input
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder={unit === 'metric' ? 'Enter weight in kg' : 'Enter weight in lbs'}
                  min="0"
                  step="0.1"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent text-lg"
                />
              </div>

              {/* Height Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Height {unit === 'metric' ? '(cm)' : '(ft/in)'}
                </label>
                {unit === 'metric' ? (
                  <input
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    placeholder="Enter height in cm"
                    min="0"
                    step="0.1"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent text-lg"
                  />
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="number"
                      value={feet}
                      onChange={(e) => setFeet(e.target.value)}
                      placeholder="Feet"
                      min="0"
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent text-lg"
                    />
                    <input
                      type="number"
                      value={inches}
                      onChange={(e) => setInches(e.target.value)}
                      placeholder="Inches"
                      min="0"
                      max="11"
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent text-lg"
                    />
                  </div>
                )}
              </div>

              {/* Optional Fields */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Age (optional)
                  </label>
                  <input
                    type="number"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    placeholder="Enter age"
                    min="0"
                    max="120"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gender (optional)
                  </label>
                  <select
                    value={gender}
                    onChange={(e) => setGender(e.target.value as 'male' | 'female')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
              </div>

              {/* Result */}
              {result && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  {/* BMI Score */}
                  <div className={`p-6 rounded-lg border ${getBMIBgColor(result.bmi)}`}>
                    <div className="text-center">
                      <div className={`text-4xl font-bold mb-2 ${getBMIColor(result.bmi)}`}>
                        {result.bmi}
                      </div>
                      <div className="text-lg font-semibold text-gray-900 mb-2">
                        {result.category}
                      </div>
                      <div className="text-sm text-gray-600">
                        {result.description}
                      </div>
                    </div>
                  </div>

                  {/* Healthy Weight Range */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="h-4 w-4 text-blue-600" />
                      <span className="font-medium text-blue-800">Healthy Weight Range</span>
                    </div>
                    <div className="text-sm text-blue-700">
                      {formatWeight(result.healthyWeightRange.min)} - {formatWeight(result.healthyWeightRange.max)}
                    </div>
                  </div>

                  {/* Weight Recommendations */}
                  {(result.weightToLose || result.weightToGain) && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="h-4 w-4 text-yellow-600" />
                        <span className="font-medium text-yellow-800">Recommendation</span>
                      </div>
                      <div className="text-sm text-yellow-700">
                        {result.weightToLose && (
                          <span>Consider losing {formatWeight(result.weightToLose)} to reach a healthy weight range.</span>
                        )}
                        {result.weightToGain && (
                          <span>Consider gaining {formatWeight(result.weightToGain)} to reach a healthy weight range.</span>
                        )}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Information */}
              <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-gray-700">
                    <p className="font-medium mb-1">Important Note:</p>
                    <p className="text-xs">
                      BMI is a screening tool and doesn't diagnose body fatness or health. 
                      Consult a healthcare provider for health assessments. BMI may not be accurate 
                      for athletes, pregnant women, or elderly individuals.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* BMI Categories */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4"
            >
              <h3 className="font-semibold text-gray-900 mb-4">BMI Categories</h3>
              <div className="space-y-3">
                {bmiRanges.map((range, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border ${
                      result && result.bmi && 
                      ((range.range === 'Below 18.5' && result.bmi < 18.5) ||
                       (range.range === '18.5 - 24.9' && result.bmi >= 18.5 && result.bmi < 25) ||
                       (range.range === '25.0 - 29.9' && result.bmi >= 25 && result.bmi < 30) ||
                       (range.range === '30.0 - 34.9' && result.bmi >= 30 && result.bmi < 35) ||
                       (range.range === '35.0 - 39.9' && result.bmi >= 35 && result.bmi < 40) ||
                       (range.range === '40.0 and above' && result.bmi >= 40))
                        ? `bg-${range.color}-100 border-${range.color}-300` 
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="font-medium text-sm text-gray-900">{range.range}</div>
                    <div className="text-xs text-gray-600">{range.category}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Health Tips */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4"
            >
              <h3 className="font-semibold text-gray-900 mb-3">Health Tips</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <div>• Maintain a balanced diet with fruits and vegetables</div>
                <div>• Exercise regularly (150 min/week moderate activity)</div>
                <div>• Stay hydrated and get adequate sleep</div>
                <div>• Avoid smoking and limit alcohol consumption</div>
                <div>• Consult healthcare providers for personalized advice</div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
          className="mt-8 sm:mt-12"
        />
      </div>
    </div>
  );
};

export default BMICalculator;
